// exclude-from-index
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Text, SVGIcon } from '@custom-components';
import { withTheme } from 'styled-components';
import withStyles from '@mui/styles/withStyles';

import { flowRight as compose } from 'lodash';
import { DesktopWrapper, HomeLinkContainer, Spacer, MenuContainer, SideNavIcon } from './DesktopNav.styles';
import NavLink from '../common/NavLink';
import Logo from '../common/Logo';
import UserSubMenu from './UserSubMenu';
import UserCard from '../common/UserCard';
import { colors } from '../../../css';
import { EnvBanner } from '../../../../components/EnvBanner';

export const linkPropType = PropTypes.shape({
  id: PropTypes.string,
  href: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  target: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.string,
  routeMatches: PropTypes.arrayOf(PropTypes.string)
});

class DesktopNav extends Component {
  static createNavLinks(links, cbFunc) {
    // if triggered again with no links, return null right away.
    if (links === null || links === undefined) {
      return null;
    }
    const extraCallback = typeof cbFunc === 'function' ? cbFunc : () => {};

    // handles if there is an array of links to make.
    if (Array.isArray(links)) {
      const reactLinks = [];

      links.forEach((linkObj) => {
        const interceptedOnClick =
          linkObj.onClick && typeof linkObj.onClick === 'function'
            ? (e) => {
                linkObj.onClick(e, extraCallback);
              }
            : extraCallback;

        reactLinks.push(
          <NavLink
            key={linkObj.id}
            href={linkObj.href || '#'}
            onClick={interceptedOnClick}
            className={linkObj.className || null}
            target={linkObj.target || null}
          >
            {linkObj.label}
          </NavLink>
        );
      });

      return reactLinks;
    }

    // handles single link
    if (!Array.isArray(links) && typeof links === 'object') {
      const interceptedOnClick =
        links.onClick && typeof links.onClick === 'function'
          ? (e) => {
              links.onClick(e, extraCallback);
            }
          : extraCallback;
      return (
        <NavLink
          href={links.href || '#'}
          onClick={interceptedOnClick}
          target={links.target || null}
          className={links.className || null}
        >
          {links.label}
        </NavLink>
      );
    }

    return null;
  }

  static getCurrentOrgName(userCurrentOrganization, organizations) {
    const currentOrg =
      organizations.length > 0
        ? organizations.find((organization) => organization.id === userCurrentOrganization)
        : undefined;
    const orgName = currentOrg ? currentOrg.name : undefined;

    return orgName;
  }

  constructor(props) {
    super(props);

    this.state = {
      menuOpen: '',
      currentOrgName: DesktopNav.getCurrentOrgName(props.userCurrentOrganization, props.organizations)
    };
  }

  handleMouseEnter = (menuName) => {
    this.setState({
      menuOpen: menuName
    });
  };

  handleMouseLeave = () => {
    this.setState({
      menuOpen: ''
    });
  };

  switchSideNav = () => {
    const { expandCollapseNav, sideExpanded } = this.props;
    const newState = !sideExpanded;
    expandCollapseNav(newState);
  };

  userMenu() {
    const {
      userFirstName,
      userLastName,
      userCurrentOrganization,
      userProfilePicture,
      organizationChange,
      userMenuLinks,
      joinOrganization,
      organizations,
      signOut,
      isTouchDevice,
      type,
      isAdminToken
    } = this.props;
    const { menuOpen, currentOrgName } = this.state;

    return (
      <MenuContainer
        onMouseEnter={() => {
          this.handleMouseEnter('userMenu');
        }}
        onMouseLeave={() => {
          this.handleMouseLeave();
        }}
      >
        <UserCard
          userFirstName={userFirstName}
          userLastName={userLastName}
          userCurrentOrganization={type === 'admin' || isAdminToken ? 'LL Admin' : currentOrgName}
          userProfilePicture={userProfilePicture}
          cursor="pointer"
          showCarrot={!isAdminToken}
          onClick={
            !isTouchDevice
              ? () => {}
              : (e) => {
                  this.handleMenuClick(e);
                }
          }
        />
        {menuOpen === 'userMenu' && !isAdminToken ? (
          <UserSubMenu
            type={type}
            subMenuLinks={DesktopNav.createNavLinks(userMenuLinks)}
            userCurrentOrganization={userCurrentOrganization}
            organizations={organizations}
            organizationChange={organizationChange}
            joinOrganization={DesktopNav.createNavLinks(joinOrganization)}
            signOut={DesktopNav.createNavLinks(signOut)}
          />
        ) : null}
      </MenuContainer>
    );
  }

  render() {
    const { sideExpanded, type, organizations, userCurrentOrganization } = this.props;
    const currentOrg = organizations.find((org) => org.id === userCurrentOrganization);

    return (
      <div>
        <DesktopWrapper>
          <SideNavIcon
            onClick={() => {
              this.switchSideNav();
            }}
          >
            <SVGIcon
              selectedIcon={sideExpanded ? 'arrow' : 'hamburger'}
              width="20px"
              height="20px"
              rotate={sideExpanded ? 90 : 0}
            />
          </SideNavIcon>
          <HomeLinkContainer>
            <Logo className="lodgelink-logo" />
          </HomeLinkContainer>
          <Spacer />
          <Text.HeadingSecond noMargins>
            {type === 'supplier' && (currentOrg ? currentOrg.name : '')}
          </Text.HeadingSecond>
          <Spacer />
          <EnvBanner />
          {this.userMenu()}
        </DesktopWrapper>
      </div>
    );
  }
}

DesktopNav.propTypes = {
  /**
   * Passed Component of the link that takes a user to the homepage.
   */
  logo: linkPropType,

  type: PropTypes.oneOf(['supplier', 'admin']).isRequired,

  isAdminToken: PropTypes.bool.isRequired,
  /**
   * Authenticated Users First Name.
   */

  userFirstName: PropTypes.string,

  /**
   * Authenticated Users Last Name.
   */

  userLastName: PropTypes.string,

  /**
   * Authenticated Users Currently active organization ID.
   */
  userCurrentOrganization: PropTypes.number,

  /**
   * Authenticated Users Profile Pic (optional), defaults to users initials from First/Last Name.
   */

  userProfilePicture: PropTypes.string,

  /**
   * List of Organizations the Authenticated user is associated to that when clicked sets current active organization
   */
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      permissions: PropTypes.arrayOf(PropTypes.string),
      role: PropTypes.string,
      id: PropTypes.number.isRequired
    })
  ),

  /**
   * Change handler to send back selected org so all places can be updated by parent/ancestor
   */
  organizationChange: PropTypes.func,

  /**
   * Passed component that
   */
  joinOrganization: linkPropType,

  /**
   * Link to sign out the current authenticated user.
   */
  signOut: linkPropType,

  /**
   * nav links in the user menu
   */
  userMenuLinks: PropTypes.arrayOf(linkPropType),

  /**
   * boolean to determine whether the side nav is expanded or not
   */
  sideExpanded: PropTypes.bool,

  /**
   * function to switch expanded side nav
   */
  expandCollapseNav: PropTypes.func,

  /**
   * Bool telling us if we are on a touch device or not.
   */
  isTouchDevice: PropTypes.bool
};

DesktopNav.defaultProps = {
  logo: null,
  userFirstName: '',
  userLastName: '',
  userCurrentOrganization: undefined,
  userProfilePicture: '',
  organizations: [],
  organizationChange: () => {},
  joinOrganization: null,
  signOut: null,
  userMenuLinks: [],
  sideExpanded: false,
  expandCollapseNav: () => {},
  isTouchDevice: false
};

const ComposedDesktopNav = compose(
  withStyles({
    badge: {
      paddingBottom: '1px',
      backgroundColor: colors.colorBlue
    }
  }),
  withTheme
)(DesktopNav);

export default ComposedDesktopNav;
